import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Zap, Search, X } from 'lucide-react';
import { SupportAPI } from '../../api';

// Placeholders are filled from the customer on the open conversation.
export const fillTemplate = (body, customer) => {
  const name = (customer?.name || '').trim();
  const first = name.split(/\s+/)[0] || 'there';
  return body
    .replace(/\{\{\s*first_name\s*\}\}/gi, first)
    .replace(/\{\{\s*name\s*\}\}/gi, name || 'there')
    .replace(/\{\{\s*email\s*\}\}/gi, customer?.email || '')
    .replace(/\{\{\s*phone\s*\}\}/gi, customer?.phone || '');
};

const CannedReplyPicker = ({ customer, onPick }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open || items) return;
    SupportAPI.cannedReplies().then(setItems).catch(() => setItems([]));
  }, [open, items]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const shown = useMemo(() => {
    const list = items || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((r) => `${r.title} ${r.body}`.toLowerCase().includes(needle));
  }, [items, q]);

  const pick = (r) => {
    onPick(fillTemplate(r.body, customer));
    SupportAPI.markCannedUsed(r.id).catch((e) => console.debug('[canned] usage bump failed', e));
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="canned-open"
        title="Insert a saved reply"
        className="p-2.5 rounded-md border border-[#D5D7ED] text-[#0A1551] hover:bg-[#F0F1FB] transition-colors" aria-label="Saved replies">
        <Zap className="w-4 h-4" />
      </button>

      {open && (
        <div data-testid="canned-menu"
          className="absolute bottom-[calc(100%+8px)] right-0 w-[360px] max-h-[340px] bg-white border border-[#E3E5F5] rounded-xl shadow-[0_12px_32px_rgba(10,21,81,0.16)] flex flex-col overflow-hidden z-20">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E3E5F5]">
            <Search className="w-3.5 h-3.5 text-[#9599C0]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search saved replies"
              data-testid="canned-search" className="flex-1 text-[13px] outline-none placeholder:text-[#9599C0]" />
            <button type="button" onClick={() => setOpen(false)} data-testid="canned-close" className="text-[#9599C0] hover:text-[#0A1551]" aria-label="Close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="overflow-y-auto">
            {items === null ? (
              <p className="p-4 text-[13px] text-[#6F76A7]">Loading…</p>
            ) : shown.length === 0 ? (
              <p className="p-4 text-[13px] text-[#6F76A7]" data-testid="canned-empty">
                {items.length === 0 ? 'No saved replies yet — an admin can add them under Saved replies.' : 'Nothing matches that search.'}
              </p>
            ) : shown.map((r) => (
              <button key={r.id} type="button" onClick={() => pick(r)} data-testid={`canned-item-${r.id}`}
                className="w-full text-left px-3 py-2.5 border-b border-[#F0F1FB] last:border-0 hover:bg-[#F9FAFD] transition-colors">
                <p className="text-[13px] font-semibold text-[#0A1551]">{r.title}</p>
                <p className="text-[12px] text-[#6F76A7] line-clamp-2">{fillTemplate(r.body, customer)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CannedReplyPicker;
